import type { LucideIcon } from "lucide-react-native";
import { MoonFormField } from "./MoonFormField";
import { MoonFieldInput } from "./MoonFieldInput";
import { FieldInput } from "./FieldInput";
import { useTranslations } from "@src/packages/i18n";

type Props = {
  label: string;
  labelIcon?: LucideIcon;
  /** The draft is MM/YYYY or YYYY; the form retains its API precision mapping. */
  precision: "month" | "year";
  value: string;
  onChange: (draft: string) => void;
  error?: string;
  hint?: string;
  disabled?: boolean;
  testID?: string;
};

/** Canonical date entry for a patient who only knows a month or a year. */
export function PartialDateField({
  label,
  labelIcon,
  precision,
  value,
  onChange,
  error,
  hint,
  disabled,
  testID,
}: Props) {
  const t = useTranslations();
  return (
    <MoonFormField
      label={label}
      labelIcon={labelIcon}
      error={error}
      hint={hint}
      disabled={disabled}
      testID={testID}
    >
      <MoonFieldInput hasError={!!error}>
        <FieldInput
          value={value}
          accessibilityLabel={label}
          onChangeText={(input) => {
            const digits = input
              .replace(/\D/g, "")
              .slice(0, precision === "month" ? 6 : 4);
            onChange(
              precision === "month" && digits.length > 2
                ? `${digits.slice(0, 2)}/${digits.slice(2)}`
                : digits,
            );
          }}
          placeholder={t(
            precision === "month"
              ? "date_picker.month_year_pattern"
              : "date_picker.year_pattern",
          )}
          keyboardType="number-pad"
          inputMode="numeric"
          maxLength={precision === "month" ? 7 : 4}
          editable={!disabled}
          dimWhenDisabled={false}
          textRole="adaptive"
          layoutStyle={{ minWidth: 0, paddingVertical: 12 }}
        />
      </MoonFieldInput>
    </MoonFormField>
  );
}
